import GenderRedirectNotification from 'components/GenderRedirectNotification'
import Loading from 'components/Loading'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useObserver } from 'mobx-react-lite'
import React from 'react'
import MixedTileRow from 'sharedComponents/ProductStream/MixedTileRow'
import ProductStreamFooter from 'sharedComponents/ProductStream/ProductStreamFooter'
import Stream from 'sharedComponents/ProductStream/Stream'
import { useStreamIndexOffset } from 'sharedComponents/ProductStream/helpers'
import { useSearchPageStore, useUrlManagerStore } from 'store/context'
import { ActiveImageTypeContext } from 'store/features/imageView/context'
import styled from 'styled-components'
import { RowTypes } from 'uiComponents/molecules/TileGrid/RowBuilder'
import EmptyResult from '../components/EmptyResult/EmptyResult'
import NoFilterResults from '../components/NoFilterResults'

import ProductStreamHeaderContent from 'sharedComponents/ProductStream/ProductStreamHeaderContent'
import ErrorBoundary from 'sharedComponents/Utils/ErrorBoundary'
import { useSearchTerm } from '../useSearchTerm'

const LoadingWrapper = styled.div`
  height: 100%;
  position: relative;
`

const SearchProductList: React.FC = () => {
  const {
    activeImageType,
    streamLoading,
    productIds,
    rowBuilder,
    resultIsEmpty,
  } = useSearchProductListData()
  const { t } = useTranslation()
  const urlManagerStore = useUrlManagerStore()
  const searchPageStore = useSearchPageStore()
  const searchTerm = useSearchTerm()
  const { productIndexOffset, rowIndexOffset } = useStreamIndexOffset(
    searchPageStore.pagination,
  )

  const termFromSelectedFilters = useSearchPageStore(
    store => store.filter.selectedFilters.term,
  )
  const searchTermFromFilter =
    termFromSelectedFilters && termFromSelectedFilters[0]

  const noFilterResults = useObserver(
    () =>
      searchPageStore.filter.isFiltered &&
      searchPageStore.pagination.totalPages === 0 &&
      !searchPageStore.streamLoading,
  )

  if (streamLoading && (!productIds || productIds.length === 0)) {
    return (
      <LoadingWrapper>
        <Loading isCentered={true} />
      </LoadingWrapper>
    )
  }

  return (
    <ActiveImageTypeContext.Provider value={activeImageType}>
      <ErrorBoundary>
        <ProductStreamHeaderContent
          title={`${t('Deine Suche nach:')} "${searchTerm}"`}
        />
      </ErrorBoundary>
      {urlManagerStore.params.gender_redirect && (
        <GenderRedirectNotification
          searchTerm={searchTermFromFilter || ''}
          redirectionStatus={urlManagerStore.params.gender_redirect}
        />
      )}
      <div className="col-sm-12">
        {noFilterResults && (
          <NoFilterResults
            productStreamStore={searchPageStore}
            clearFilter={searchPageStore.filter.clearFilter}
          />
        )}
      </div>
      {resultIsEmpty ? (
        <EmptyResult />
      ) : (
        <Stream
          loading={streamLoading}
          firstBatchProductIds={productIds}
          rowBuilder={rowBuilder}
          rowComponentMap={{
            [RowTypes.StreamRow]: MixedTileRow,
          }}
          productIndexOffset={productIndexOffset}
          rowIndexOffset={rowIndexOffset}
        />
      )}
      <ProductStreamFooter />
    </ActiveImageTypeContext.Provider>
  )
}

const useSearchProductListData = () => {
  const searchPageStore = useSearchPageStore()

  const resultIsEmpty = useObserver(
    () =>
      !searchPageStore.filter.isFiltered &&
      searchPageStore.pagination.totalPages === 0,
  )

  return useObserver(() => ({
    activeImageType: searchPageStore.activeImageType,
    streamLoading: searchPageStore.streamLoading,
    productIds: searchPageStore.productIds,
    rowBuilder: searchPageStore.rowBuilder,
    resultIsEmpty,
  }))
}

export default SearchProductList
